/**
 * SpacesSwitcher — orchestrator that picks the correct spaces navigation variant
 * based on viewport width.
 *
 * ≥768px → SpacesTabStrip (desktop tab strip above the board)
 * <768px → pill bar (bottom-fixed on phone)
 *
 * BOTH variants share state via useSpacesSwitcher (PromptMode, popover, inline rename).
 *
 * TV mode: if isTV=true, renders null (AFTER all hooks per HIGH-1 ruling).
 *
 * UX RULINGS (unchanged):
 *   - VISIBLE IN EDIT MODE: spaces are navigation, not an edit-mode concern.
 *   - HIDDEN IN TV MODE: isTV guard (placed after hooks).
 *
 * Phone pill bar:
 *   - position: fixed; bottom: 0 regardless of DOM order (DOM order is above board).
 *   - iOS segmented-control idiom: sliding thumb, roving tabindex, arrow keys, Enter/Space.
 *   - aria-current="page" on active pill within nav aria-label="Spaces".
 *
 * Immutability: pure controlled component; all state changes go through actions.
 */
import { useRef, useState, useLayoutEffect, useCallback, useEffect } from 'react';
import type { Space } from 'briefpane/schema';
import { MAX_SPACES } from 'briefpane/schema';
import type { SpacesActions } from '../board/use-spaces.js';
import { useSpacesSwitcher } from './use-spaces-switcher.js';
import { SpacesTabStrip } from './SpacesTabStrip.js';
import { SpacePopover } from './SpacePopover.js';
import { SpaceNamePrompt } from './SpaceNamePrompt.js';

interface SpacesSwitcherProps {
  readonly spaces: readonly Space[];
  readonly activeSpaceId: string;
  readonly actions: SpacesActions;
  /** If true, the switcher is hidden (TV mode ruling). */
  readonly isTV: boolean;
}

/** Detect prefers-reduced-motion. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Use a matchMedia query and track it with React state. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => { setMatches(e.matches); };
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => { mql.removeEventListener('change', handler); };
  }, [query]);

  return matches;
}

export function SpacesSwitcher({
  spaces,
  activeSpaceId,
  actions,
  isTV,
}: SpacesSwitcherProps): React.ReactElement | null {
  // HIGH-1 FIX: ALL hooks must be called before any early return.

  const isDesktop = useMediaQuery('(min-width: 768px)');
  const logic = useSpacesSwitcher(spaces, activeSpaceId, actions);

  // Phone pill bar state (only active when isPhone).
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbStyle, setThumbStyle] = useState<React.CSSProperties>({});
  const reduced = prefersReducedMotion();

  // Compute thumb position over the active pill (phone only).
  useLayoutEffect(() => {
    if (isDesktop) return;
    if (!trackRef.current) return;
    const track = trackRef.current;
    const activeIdx = spaces.findIndex((s) => s.id === activeSpaceId);
    if (activeIdx < 0) return;
    const pills = track.querySelectorAll<HTMLElement>('[data-space-pill]');
    const pill = pills[activeIdx];
    if (!pill) return;
    const trackRect = track.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();
    setThumbStyle({
      left: pillRect.left - trackRect.left,
      width: pillRect.width,
    });
  }, [spaces, activeSpaceId, isDesktop]);

  // Roving tab index: arrow-key navigation (phone pill bar).
  const pillsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const handlePillKeyDown = useCallback((e: React.KeyboardEvent, idx: number) => {
    const n = spaces.length;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      pillsRef.current[(idx + 1) % n]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      pillsRef.current[(idx - 1 + n) % n]?.focus();
    }
  }, [spaces.length]);

  // HIGH-1 FIX: guard placed AFTER all hooks.
  if (isTV) return null;

  const {
    promptMode,
    promptError,
    popover,
    handlePromptConfirm,
    handlePromptCancel,
    openPopover,
    handlePopoverRename,
    handlePopoverDuplicate,
    handlePopoverDelete,
    closePopover,
    setPromptMode,
    setPromptError,
  } = logic;

  // Desktop: render the tab strip.
  if (isDesktop) {
    return (
      <SpacesTabStrip
        spaces={spaces}
        activeSpaceId={activeSpaceId}
        actions={actions}
        logic={logic}
        isTV={isTV}
      />
    );
  }

  // Phone: render the pill bar (position: fixed at bottom via CSS class).
  const atLimit = spaces.length >= MAX_SPACES;

  return (
    <>
      {/* Phone pill bar — fixed at bottom via .spaces-pill-bar CSS class */}
      <div className="spaces-pill-bar">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '8px 16px 10px',
          }}
        >
          {/* MEDIUM-3: <nav> with aria-label="Spaces" replaces role="tablist" */}
          <nav
            aria-label="Spaces"
            ref={trackRef}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'var(--color-bg-input)',
              borderRadius: '10px',
              padding: '2px',
              gap: '0',
            }}
          >
            {/* Sliding thumb (behind pills) */}
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '2px',
                bottom: '2px',
                borderRadius: '8px',
                backgroundColor: 'var(--color-bg-tile)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                transition: reduced
                  ? 'none'
                  : `left var(--dur-settle) var(--spring-settle), width var(--dur-settle) var(--spring-settle)`,
                pointerEvents: 'none',
                ...thumbStyle,
              }}
            />

            {spaces.map((space, idx) => {
              const isActive = space.id === activeSpaceId;
              return (
                <div key={space.id} style={{ position: 'relative', zIndex: 1 }}>
                  <button
                    ref={(el) => { pillsRef.current[idx] = el; }}
                    data-space-pill
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={space.name}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => { actions.switchSpace(space.id, 'pill'); }}
                    onKeyDown={(e) => { handlePillKeyDown(e, idx); }}
                    style={{
                      minHeight: '32px',
                      padding: '0 12px',
                      background: 'none',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: 'var(--text-delta)',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      position: 'relative',
                    }}
                  >
                    {space.name}
                  </button>
                  {/* ⋯ affordance: opens SpacePopover */}
                  <button
                    type="button"
                    aria-label={`Options for ${space.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openPopover(space.id, e.currentTarget);
                    }}
                    style={{
                      position: 'absolute',
                      top: '-14px',
                      right: '2px',
                      width: '20px',
                      height: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '9px',
                      color: 'var(--color-text-dim)',
                      padding: 0,
                      lineHeight: 1,
                    }}
                  >
                    {/* L2 fix: wrap decorative glyph in aria-hidden span */}
                    <span aria-hidden="true">···</span>
                  </button>
                </div>
              );
            })}

            {/* "+" add button */}
            <button
              type="button"
              aria-label="Add a space"
              tabIndex={0}
              disabled={atLimit}
              title={atLimit ? `Limit of ${MAX_SPACES} spaces reached` : 'Add a new space'}
              onClick={() => {
                if (!atLimit) {
                  setPromptMode({ kind: 'create' });
                  setPromptError(null);
                }
              }}
              style={{
                minHeight: '32px',
                padding: '0 10px',
                background: 'none',
                border: 'none',
                borderRadius: '8px',
                fontSize: 'var(--text-label)',
                color: atLimit ? 'var(--color-text-dim)' : 'var(--color-text-secondary)',
                cursor: atLimit ? 'not-allowed' : 'pointer',
                opacity: atLimit ? 0.4 : 1,
                position: 'relative',
                zIndex: 1,
              }}
            >
              +
            </button>
          </nav>
        </div>
      </div>

      {/* Popover (context menu for a space) */}
      {popover && (() => {
        const space = spaces.find((s) => s.id === popover.spaceId);
        if (!space) return null;
        return (
          <SpacePopover
            spaceId={popover.spaceId}
            spaceName={space.name}
            isLast={spaces.length <= 1}
            anchorRect={popover.anchorRect}
            onRename={() => { handlePopoverRename(popover.spaceId, space.name); }}
            onDuplicate={() => { handlePopoverDuplicate(popover.spaceId); }}
            onDelete={() => { handlePopoverDelete(popover.spaceId); }}
            onClose={closePopover}
          />
        );
      })()}

      {/* Name prompt (create or rename) */}
      {promptMode && (
        <SpaceNamePrompt
          title={promptMode.kind === 'create' ? 'New space' : 'Rename space'}
          initialValue={promptMode.kind === 'rename' ? promptMode.currentName : ''}
          errorMessage={promptError}
          onConfirm={handlePromptConfirm}
          onCancel={handlePromptCancel}
        />
      )}
    </>
  );
}
